import React, { Fragment, useMemo } from 'react';
import { useSelector } from 'react-redux';
import T from 'prop-types';
import { some, head, last, isEmpty } from 'lodash-es';

import EmptyBox from './EmptyBox';
import Skeleton from '../../../Skeleton';
import ArrowChange from '../../../ArrowChange';
import SmartChart from '../../../Chart/SmartChart';
import defaultLineChartConfig from '../../../Chart/LineChart';
import SimpleDropdown from '../../../Dropdowns/SimpleDropdown';

import { permissionsSelector } from '../../selectors';
import { makeMetricOptions, getMetricName, valueFormatter } from '../../configs/pub-quickview';
import { format } from '../../../../utils/date';

const emptyObject = {};

const shortFormat = 'M/D/YYYY';

const periodString = (range) => {
  if (isEmpty(range)) return 'Last 30 d';
  return `${format(head(range)[0], shortFormat)} — ${format(last(range)[0], shortFormat)}`;
};

const makeChartConfig = ({ metric, previous_period_data = [] }) =>
  defaultLineChartConfig({
    chartOptions: {
      height: 174,
      marginTop: 20,
      plotAreaheight: 118,
      spacingLeft: 0,
    },
    xAxisOptions: {
      tickWidth: 0,
    },
    xAxisLabels: {
      y: 30,
      fontSize: 10,
      color: '#848EA1',
      formatter() {
        return Highcharts.dateFormat('%m/%d', this.value);
      },
    },
    yAxisLabels: {
      tickmarkPlacement: 'on',
      fontSize: 10,
      color: '#848EA1',
    },
    yAxisOptions: {
      tickAmount: 5,
      gridLineWidth: 1,
      gridLineColor: '#EDF0F5',
      visible: true,
      lineWidth: 0,
      offset: 0,
      ceiling: metric === 'eng' ? 100 : Infinity,
    },
    tooltipOptions: {
      borderWidth: 0,
      padding: 0,
      shared: true,
      valueDecimals: 2,
      useHTML: true,
      outside: true,
      formatter() {
        let rows;

        const template = (point) =>
          `<tr>
          <td class="text-left text-black text-14 p-l-0 p-b-0 pr-md">
            <div class="text-truncate text-black text-14" style='max-width: 100%'>
              <span
                class="tooltip-bubble-marker"
                style="
                  background-color: ${point.color === '#15385B' ? '#fff' : point.series.color};
                  border-color: ${point.color};"
                >
              </span>
              <span>${format(
                point.color !== '#15385B' ? point.x : previous_period_data[point.point.index][0],
                'MMM DD, YYYY'
              )}</span>
            </div>
          </td>
          <td class="text-right text-black text-14 p-l-0 p-b-0">
            ${valueFormatter(metric === 'total_time_spent' ? point.y * 3600 : point.y, metric)}
          </td>
        </tr>`;

        if (Array.isArray(this.points)) {
          rows = this.points.map((point) => template(point));
        } else return false;

        return `
        <div
          style="
            min-width: 189px;
            border-radius: 8px;
            border: 1px solid #E1E5EB;
            padding: 16px;
            background: #fff;
            box-shadow: 0px 0px 24px 0px rgba(0, 0, 0, 0.08);
          "
        >
          <div style="
          font-size: 16px;
          font-style: normal;
          font-weight: 700;
          line-height: 150%;
          margin-bottom: 16px"
          class="text-black">
          ${getMetricName(metric)}
          </div>
          <table class="table">${rows.join('')}</table>
        </div>
      `;
      },
    },
  });

const PubLineChart = ({ settings, metric, timeSeries, onTypeChange }) => {
  const permissions = useSelector(permissionsSelector);

  const activeChartData = timeSeries[metric] || emptyObject;
  const showData = !settings.pending && !settings.error;

  const metaSeriesData = useMemo(
    () => ({
      current_period_data: {
        checked: true,
        color: '#348BE3',
        name: getMetricName(metric),
        visible: true,
      },
      prev_period_data: {
        name: 'Previous',
        checked: true,
        color: '#15385B',
        visible: true,
        className: 'dashed-line',
        dashStyle: 'ShortDash',
      },
    }),
    [metric]
  );

  const { current_period_data = [], previous_period_data = [] } = activeChartData;

  const chartConfig = useMemo(
    () => makeChartConfig({ metric, previous_period_data }),
    [metric, previous_period_data]
  );

  const series = useMemo(() => {
    if (metric === 'total_time_spent') {
      return {
        current_period_data: current_period_data.map((current) => [current[0], current[1] / 3600]),
        prev_period_data: current_period_data.map((current, i) => [
          current[0],
          previous_period_data[i][1] / 3600,
        ]),
      };
    }
    return {
      current_period_data,
      prev_period_data: current_period_data.map((current, i) => [
        current[0],
        previous_period_data[i][1],
      ]),
    };
  }, [activeChartData]);

  const prevPeriod = useMemo(() => periodString(previous_period_data), [activeChartData]);
  const currPeriod = useMemo(() => periodString(current_period_data), [activeChartData]);

  const dataPresent = some(series, (seriesValues) =>
    seriesValues.some(([, pointData]) => pointData)
  );

  const metricOptions = useMemo(() => makeMetricOptions(permissions), [permissions]);

  return (
    <div className="prospects-sidebar__chart prospects-sidebar__box">
      <div className="prospects-sidebar__chart-head">
        <SimpleDropdown
          selected={metric}
          options={metricOptions}
          frameless
          menuWidth={270}
          className="ignore-onclickoutside"
          onChange={onTypeChange}
        />
        <div className="prospects-sidebar__chart-head-period">{currPeriod}</div>
      </div>
      {settings.pending && (
        <Fragment>
          <div className="prospects-sidebar__chart-change">
            <Skeleton height={46} width={125} />
          </div>
          <div className="prospects-sidebar__chart">
            <Skeleton height={185} />
          </div>
        </Fragment>
      )}

      {showData &&
        (dataPresent ? (
          <Fragment>
            <div className="prospects-sidebar__chart-change">
              <ArrowChange
                change={activeChartData.change || 0}
                iconPosition="behind"
                upArrowClass="hicon-positive-arrow"
                downArrowClass="hicon-negative-arrow"
                showMathOperators
                precision={3}
                className="line-chart-change"
              />
            </div>
            <div className="prospects-sidebar__chart-total">
              {valueFormatter(activeChartData.total, metric)} (estimated)
            </div>
            <SmartChart
              chartId="prospects-sidebar__chart"
              series={series}
              metaSeriesData={metaSeriesData}
              config={chartConfig}
            />
            <div className="prospects-sidebar__chart-legend">
              <span className="prospects-sidebar__chart-legend-circle" />
              Previous Period
              <div className="prospects-sidebar__chart-legend-period">({prevPeriod})</div>
            </div>
          </Fragment>
        ) : (
          <EmptyBox text="No categories detected" />
        ))}
    </div>
  );
};

PubLineChart.propTypes = {
  settings: T.object.isRequired,
  metric: T.string.isRequired,
  timeSeries: T.object.isRequired,
  onTypeChange: T.func.isRequired,
};

export default PubLineChart;
